import { ExpandMore, Logout, Person } from '@mui/icons-material'
import {
  Box,
  Collapse,
  Drawer,
  lighten,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { nanoid } from 'nanoid'
import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import preloadAccountPortalRoutes from '~/app/utils/lazy/accountPortalLazyLoadingUtils'
import useSignOut from '~/features/auth/hooks/useSignOut'
import CreateOrModifyLibbyButton from '~/features/marketing/components/createOrModifyLibbyButton'
import usePartnerCentralized from '~/features/partners/hooks/usePartnerCentralized'
import useGetElementDimensions from '~/features/ui/hooks/useGetElementDimensions'
import EElement from '~/features/ui/models'
import useGetAuthSessionQuery from '~/hooks/useGetAuthSessionQuery'
import Social from '~/layouts/app/footer/termsAndSocial/social'
import navItems from '~/layouts/app/utils'
import { EClientPath } from '~/models/api'
import rem from '~/utils/ui'

type TProps = {
  isOpened: boolean
  close: () => void
}

function SmallScreenNav({ isOpened, close }: TProps) {
  const { partner } = usePartnerCentralized()
  const { height: headerHeight } = useGetElementDimensions(EElement.appHeader)
  const { data: authSession } = useGetAuthSessionQuery()
  const isLoggedIn = !!authSession?.email
  const { signOut } = useSignOut()
  const theme = useTheme()
  const navigate = useNavigate()
  const [isHelpSubmenuOpen, setIsHelpSubmenuOpen] = useState<boolean>(false)
  const [isWhoWeHelpSubmenuOpen, setIsWhoWeHelpSubmenuOpen] =
    useState<boolean>(false)

  const style = {
    width: '100vw',
    height: '100%',
    maxWidth: theme.breakpoints.values.xs,
    pt: `calc(${rem(headerHeight)} + ${rem(24)})`,
    pl: rem(24),
    pb: rem(24),
    background: theme.palette.mainFont,
    justifyContent: 'space-between',
  }

  const linkStyle = {
    color: theme.palette.common.white,
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '20px',
  }

  const handleSubmenuClick = (menuName: string) => {
    if (menuName === 'Help') {
      setIsWhoWeHelpSubmenuOpen(false)
      setIsHelpSubmenuOpen(!isHelpSubmenuOpen)
    } else if (menuName === 'Who We Help') {
      setIsHelpSubmenuOpen(false)
      setIsWhoWeHelpSubmenuOpen(!isWhoWeHelpSubmenuOpen)
    }
  }

  const handleMyAccountMouseOver = () => {
    preloadAccountPortalRoutes()
  }

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={isOpened}
      onClose={close}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
      sx={{ display: { xxxs: 'inherit', lg: 'none' } }}
    >
      <Stack sx={style}>
        <Box>
          <List component="nav" sx={{ mb: rem(30) }}>
            {navItems.map(({ name, path, children }) => (
              <Box key={nanoid()}>
                <ListItemButton
                  key={name}
                  component={children ? 'div' : NavLink}
                  to={path ?? ''}
                  onClick={children ? () => handleSubmenuClick(name) : close}
                  sx={{
                    '& svg': {
                      color: theme.palette.common.white,
                      transition: 'transform 0.3s ease',
                      transform:
                        (name === 'Help' && isHelpSubmenuOpen) ||
                        (name === 'Who We Help' && isWhoWeHelpSubmenuOpen)
                          ? 'rotate(180deg)'
                          : 'rotate(0deg)',
                    },
                  }}
                >
                  <ListItemText
                    primary={name}
                    primaryTypographyProps={{ sx: linkStyle }}
                  />

                  {children && <ExpandMore />}
                </ListItemButton>
                {children && (
                  <Collapse
                    in={
                      name === 'Help'
                        ? isHelpSubmenuOpen
                        : isWhoWeHelpSubmenuOpen
                    }
                    timeout="auto"
                    unmountOnExit
                    sx={{
                      marginLeft: rem(-24),
                    }}
                  >
                    <List component="div" disablePadding>
                      {children.map((item) => (
                        <ListItemButton
                          key={item.name}
                          component={NavLink}
                          to={item.path ?? ''}
                          onClick={close}
                          sx={{
                            pl: 4,
                            background: lighten(
                              theme.palette.menuBackground,
                              0.1,
                            ),
                            '&:hover': {
                              background: lighten(
                                theme.palette.menuBackground,
                                0.3,
                              ),
                            },
                            '& span': {
                              color: theme.palette.common.white,
                              fontSize: rem(19),
                              fontWeight: 500,
                              paddingLeft: rem(20),
                            },
                          }}
                        >
                          <ListItemText primary={item.name} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
              </Box>
            ))}
          </List>

          {!isLoggedIn && <CreateOrModifyLibbyButton />}

          {isLoggedIn && (
            <List
              style={{ marginTop: `auto`, color: theme.palette.common.white }}
            >
              <ListItemButton onClick={() => navigate(EClientPath.overview)}>
                <ListItemIcon>
                  <Person
                    sx={{ color: theme.palette.common.white }}
                    fontSize="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary="My account"
                  onMouseOver={handleMyAccountMouseOver}
                />
              </ListItemButton>
              <ListItemButton onClick={signOut}>
                <ListItemIcon>
                  <Logout
                    sx={{ color: theme.palette.common.white }}
                    fontSize="small"
                  />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </ListItemButton>
            </List>
          )}
        </Box>
        <Box>
          <Typography
            sx={{
              color: theme.palette.common.white,
              fontSize: '0.8rem',
              mt: '2rem',
              mb: '2rem',
            }}
          >
            &copy; 2025 {partner.domain}. All rights reserved.
          </Typography>
          <Social />
        </Box>
      </Stack>
    </Drawer>
  )
}

export default SmallScreenNav
